import { useContext } from '@berlin-front/common/composables/context';
import { computed, reactive, h, defineComponent } from '@vue/composition-api';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { windowOpenNewTab } from '@berlin-front/common/utils';
import { broadcast } from '@/broadcast';
import { AtoneAuthenticateGetResponseMemberTypeEnum } from '@netprotections/berlin-bff';
import AtoneNgDetailVue from '@views/fail/auth-ng/detail-link/AtoneNgDetail.vue';
import AuthFailDetailAppDownload from '@views/fail/auth-ng/detail-link/AuthFailDetailAppDownload.vue';
import AuthFailDetailReason from '@views/fail/auth-ng/detail-link/AuthFailDetailReason.vue';
import AuthFailDetailMyPage from '@views/fail/auth-ng/detail-link/AuthFailDetailMyPage.vue';
import AuthFailDetailNpMyPage from '@views/fail/auth-ng/detail-link/AuthFailDetailNpMyPage.vue';
import { AuthNgFailContextType } from './composable';

/**
 * 仮会員でマイページリンク表示対象とするオーソリ結果NG理由
 *
 * - 未払い -> 2
 */
const SHOW_MY_PAGE_NG_REASON_FOR_LITE: readonly number[] = [2] as const;

const atoneLiteAuthNgFail: AuthNgFailContextType = {
  useAuthNgFail: () => {
    const {
      liteCapturePostResult,
      atoneAuthenticateGetResult,
      guestRegisterPostResult,
      guestAuthenticationTokenGetResult,
      guestSmsAuthenticateVerifyPostResult,
    } = useContext(ApiCacheContext);

    /**
     * ゲストかどうか。
     */
    const isGuest = computed(
      () =>
        // preToken認証でguest判定だった場合
        atoneAuthenticateGetResult.value?.memberType === AtoneAuthenticateGetResponseMemberTypeEnum.NUMBER_3 ||
        // ゲスト登録フローからきた場合
        typeof guestRegisterPostResult.value?.authenticationToken === 'string' ||
        // ゲスト認証フローでsmsスキップできた場合
        typeof guestAuthenticationTokenGetResult.value?.token === 'string' ||
        // ゲスト認証フローでsmsを行なった場合
        typeof guestSmsAuthenticateVerifyPostResult.value?.authenticationToken === 'string',
    );

    // TODO: reauthorizeはのちほど。リリース時無くてOK
    // atoneReauthorizePutResult.value?.authorizationResultNgMessage ??
    const authorizationResult = computed(() => liteCapturePostResult.value);

    const authorizationResultNgMessage = computed(() => authorizationResult.value?.authorizationResultNgMessage ?? []);

    /** アプリダウンロードリンクを表示するかどうか。 */
    const showAppDownloadLink = computed(() => authorizationResult.value?.appDownloadFlg ?? false);
    /** np会員ページリンクを表示するかどうか。 */
    const showNpMyPageLink = computed(() => authorizationResult.value?.atobaraiMemberPageFlg ?? false);
    /**
     * マイページへのリンクを出すか出さないかをユーザの状態とng_reasonから決定する。
     *
     * https://np-develop.backlog.jp/view/ATN-20572#comment-104784569
     */
    const showMyPageLink = computed(
      () =>
        // 仮会員でない場合は表示。
        !isGuest.value ||
        // 都度払いの表示対象NG_REASONに 含まれる 場合は表示。
        SHOW_MY_PAGE_NG_REASON_FOR_LITE.includes(authorizationResult.value?.authorizationResultNgReason ?? NaN),
    );
    /** エラー詳細リンクを表示するかどうか。エラー画面では常に表示 */
    const showAuthFailDetailReason = computed(() => true);

    /**
     * AtoneLiteNgDetailVueにpropsを適用したラッピングコンポーネント。
     *
     * - 共通レイヤーにliteに特化したロジックやインターフェイスを追加しないで済むように
     * - 差し込むhtml側にロジックを含ませないために
     *
     * ラップしてる。
     */
    const PropsAppliedAtoneLiteNgDetailVue = computed(() =>
      defineComponent({
        render: () =>
          h(AtoneNgDetailVue, {}, [
            showAuthFailDetailReason.value ? h(AuthFailDetailReason) : undefined,
            showNpMyPageLink.value ? h(AuthFailDetailNpMyPage) : undefined,
            showMyPageLink.value ? h(AuthFailDetailMyPage) : undefined,
            showAppDownloadLink.value ? h(AuthFailDetailAppDownload) : undefined,
          ]),
      }),
    );

    const onCancel = () => {
      broadcast.send({
        type: 'MODAL_CONTROL_FAILED',
        payload: {},
      });
    };

    const onInquiry = () => {
      windowOpenNewTab(process.env.VUE_APP_ATONE_FAQ_URL);
    };

    return reactive({
      // 表示項目
      ngReasons: authorizationResultNgMessage,
      detailComponent: PropsAppliedAtoneLiteNgDetailVue,

      // 関数
      onCancel,
      onInquiry,
    });
  },
};

export default atoneLiteAuthNgFail;
